import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { SxProps, Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';

/**
 * The four-step journey indicator shown on every lifecycle screen, so the
 * guide can always answer "where am I and what happens next?".
 *
 * One rendering everywhere: a numbered tracker with a connecting rail. The
 * flow used to also have a compact checklist variant, which made the status
 * screens look like a different, plainer product than the one the applicant
 * had just come from.
 */
export type GuideJourneyStage =
  | 'application'
  | 'verification'
  | 'profile'
  | 'review'
  | 'done';

/** Each id is also its key under `journey.steps` in the guideProfile namespace. */
const STEPS = ['application', 'verification', 'profile', 'review'] as const satisfies readonly GuideJourneyStage[];

interface GuideJourneyStepsProps {
  current: GuideJourneyStage;
  /** Marks the current step as blocked rather than in progress. */
  failed?: boolean;
  sx?: SxProps<Theme>;
}

export function GuideJourneySteps({ current, failed, sx }: GuideJourneyStepsProps) {
  const currentIndex =
    current === 'done' ? STEPS.length : STEPS.findIndex((step) => step === current);

  return <HorizontalSteps currentIndex={currentIndex} failed={failed} sx={sx} />;
}

const MARKER = 34;

/**
 * The tracker itself.
 *
 * The rail is drawn per-gap rather than as one bar behind the row, so a
 * completed leg can be coloured independently — and so it never shows through
 * the markers at small sizes. On phones the whole thing turns vertical: four
 * labels plus descriptions cannot sit side by side at 360px without wrapping
 * into something unreadable.
 */
function HorizontalSteps({
  currentIndex,
  failed,
  sx,
}: {
  currentIndex: number;
  failed?: boolean;
  sx?: SxProps<Theme>;
}) {
  const { t } = useTranslation('guideProfile');

  return (
    <Box
      sx={[
        {
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: `repeat(${STEPS.length}, 1fr)` },
          gap: { xs: 2, sm: 0 },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {STEPS.map((step, index) => {
        const complete = index < currentIndex;
        const isCurrent = index === currentIndex;
        const isBlocked = isCurrent && failed;
        const legComplete = index < currentIndex;

        return (
          <Box
            key={step}
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: { xs: 'row', sm: 'column' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1.75, sm: 0 },
              textAlign: { xs: 'left', sm: 'center' },
              px: { sm: 1 },
            }}
          >
            {/* The leg arriving at this marker, so step 1 has none. */}
            {index > 0 && (
              <Box
                aria-hidden
                sx={(theme) => ({
                  display: { xs: 'none', sm: 'block' },
                  position: 'absolute',
                  top: MARKER / 2,
                  right: '50%',
                  left: '-50%',
                  height: 2,
                  borderRadius: 1,
                  bgcolor: legComplete
                    ? 'success.main'
                    : alpha(theme.palette.text.primary, 0.12),
                })}
              />
            )}

            <Box
              sx={(theme) => ({
                position: 'relative',
                zIndex: 1,
                width: MARKER,
                height: MARKER,
                flexShrink: 0,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.875rem',
                fontWeight: 700,
                border: '2px solid',
                ...(complete
                  ? {
                      bgcolor: 'success.main',
                      borderColor: 'success.main',
                      color: 'common.white',
                    }
                  : isBlocked
                    ? {
                        bgcolor: 'error.main',
                        borderColor: 'error.main',
                        color: 'common.white',
                      }
                    : isCurrent
                      ? {
                          bgcolor: 'common.white',
                          borderColor: 'primary.main',
                          color: 'primary.main',
                          boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.16)}`,
                        }
                      : {
                          bgcolor: 'common.white',
                          borderColor: alpha(theme.palette.text.primary, 0.18),
                          color: 'text.disabled',
                        }),
              })}
            >
              {complete ? (
                <CheckIcon sx={{ fontSize: 18 }} />
              ) : isBlocked ? (
                <PriorityHighIcon sx={{ fontSize: 18 }} />
              ) : isCurrent ? (
                /* A filled dot reads as "you are here" where a number would
                   just look like one more pending step. */
                <Box
                  sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main' }}
                />
              ) : (
                index + 1
              )}
            </Box>

            <Box sx={{ mt: { sm: 1.25 }, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: isCurrent || complete ? 700 : 500,
                  lineHeight: 1.35,
                  color: complete || isCurrent ? 'text.primary' : 'text.secondary',
                }}
              >
                {t(`journey.steps.${step}.label`)}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 0.5,
                  lineHeight: 1.45,
                  color: 'text.secondary',
                }}
              >
                {t(`journey.steps.${step}.description`)}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
